<template>
  <div>
    <section v-if="settingsLoaded">
      <single-setting
        id="calendar"
        class="single-setting"
        :icon="require('@/assets/_desktop/_settings/_icons/Calendar.svg')"
        :title="$t('calendar')"
        :description="$t('setDefaultCalendarFormat')"
      >
        <dropdown
          id="start-of-week"
          :title="$t('startOfWeek')"
          :options="[
            { name: $t('weekDay')[6], value: 'Saturday' },
            { name: $t('weekDay')[0], value: 'Sunday' },
            { name: $t('weekDay')[1], value: 'Monday' },
          ]"
          dbname="week-start"
          :value="settings['week-start']"
          @input="changeUserStartOfWeek"
        />

        <dropdown
          id="timezone"
          class="span2"
          editable
          auto-complete
          :title="$t('timezone')"
          show-all
          by-value
          :options="timezones"
          :dbname="'timezone'"
          :value="timezone"
          @input="changeTimezone"
        />
        <dropdown
          id="hourFormat"
          :key="settings['is-24h'] + 'hour24h'"
          :title="$t('hourFormat')"
          :options="hourFormatOptions()"
          by-value
          dbname="is-24h"
          :value="!!settings['is-24h']"
          @input="changeHourFormat"
        />
      </single-setting>
      <!-- <single-setting
        id="work-schedule-deadline"
        class="single-setting"
        :icon="require('@/assets/_desktop/_settings/_icons/Deadline.svg')"
        :title="$t('workScheduleDeadline')"
        :description="$t('deadlineDescription')"
      >
        <team-work-schedule-deadline-list
          id="work-schedule-deadline-list"
          :disabled="true"
        />
      </single-setting> -->
      <team-work-schedule-deadline-list
        id="work-schedule-deadline-list"
        :disabled="true"
        :user-settings="$store.state.userSettings"
        :is24h="$store.state.userSettings['is-24h']"
      />
    </section>
  </div>
</template>

<script>
import User from '@/components/User'
import Constants from '@/components/Constants'
import Validation from '@/components/Validation'
import SingleSetting from '@/components/Settings/SingleSetting'
import Dropdown from '@/components/UI/Dropdown'
import TeamWorkScheduleDeadlineList from '@/components/Team/TeamWorkScheduleDeadlineList'
import Parse from 'parse'
import moment from 'moment-timezone'
import ParseFunctions from '@/components/ParseFunctions'
import { calculateStoredEntriesKey } from '../../../../clavo-shared/functions/entriesFetchTools'
import { getThisWeekStart } from '../../../../clavo-shared/functions/time'

export default {
  components: {
    SingleSetting,
    Dropdown,
    TeamWorkScheduleDeadlineList,
  },
  mixins: [User, Constants, Validation, ParseFunctions],
  props: {},
  data() {
    return {
      settings: null,
      timezones: [],
      timezone: null,
      settingsLoaded: false,
      currentWorkingHours: null,
      currentVacationDays: null,
    }
  },
  async created() {
    await this.load()
  },
  methods: {
    async load() {
      Parse.User.current().fetch()

      this.timezones = this.$store.state.timezonesGlobal
      const _this = this
      let tz = Parse.User.current().get('timezone')
      let timezoneFound = this.timezones.find((x) => x.value == tz)
      if (timezoneFound) {
        _this.timezone = timezoneFound.value
      }

      let res = await this.userSettingsCLAPI()
      if (res.status === 'ok') {
        _this.settings = res.settings
        _this.currentWorkingHours = _this.settings['default-working-hours-weekly']
        _this.currentVacationDays = _this.settings['default-holiday-days-yearly']
        _this.settingsLoaded = true
        if (!_this.settings['week-start']) _this.settings['week-start'] = 'Monday'
      } else {
        this.showServerError(res)
        return
      }

      await this.$store.dispatch('reloadTeams')

      if (!this.settings.timezone) {
        this.settings.timezone = Parse.User.current().attributes.timezone
        let resTimezone = await this.setUserSetting('timezone', this.settings.timezone)
        if (resTimezone.status !== 'ok') this.showServerError(resTimezone)
      }

      let startDate = getThisWeekStart(this.settings['week-start'], this.settings.timezone)
      let endDate = moment(startDate).tz(this.settings.timezone).add(1, 'week')

      // check if user has holidayInfo for every team
      if (!(this.$store.state.holidayInfo[this.userId] && this.$store.state.holidayInfo[this.userId].holidayInfo.length == this.$store.state.teams.length)) {
        await this.fetchEntriesAndUpdateStore(startDate, endDate)
      }
    },
    async fetchEntriesAndUpdateStore(startDate, endDate) {
      let valsToCalculateKey = {
        start: moment(startDate).tz(this.settings.timezone).utc().format('DD/MM/YYYY HH:mm:ss'),
        end: moment(endDate).tz(this.settings.timezone).utc().format('DD/MM/YYYY HH:mm:ss'),
        userId: this.userId,
      }
      let key = calculateStoredEntriesKey(valsToCalculateKey)

      const res = await this.getEntriesWithUserId(startDate.toDate(), endDate.toDate(), this.userId)

      if (res.status !== 'ok') {
        this.showServerError(res)
        return
      }

      // add schedule to the store
      let usersScheduleToStore = {
        startDate: startDate,
        endDate: endDate,
        userId: this.userId,
        schedule: res.schedule[0],
        lastLookUp: new Date().getTime(),
      }
      this.$store.commit('addWeekToBufferedUsersSchedules', { key, value: usersScheduleToStore })
      this.$store.commit('limitBufferedUsersSchedules')

      // add holidayInfo to the store
      let holidayInfoToStore = {
        holidayInfo: res.schedule[0].teams,
        lastLookUp: usersScheduleToStore.lastLookUp,
      }
      this.$store.commit('setHolidayInfo', { key: this.userId, value: holidayInfoToStore })
      this.$store.commit('limitHolidayInfo')

      // add followedList to the store
      let followedListToStore = {
        isFollowed: res.schedule[0].isFollowed,
        lastLookUp: usersScheduleToStore.lastLookUp,
      }
      this.$store.commit('setFollowedList', { key: this.userId, value: followedListToStore })
      this.$store.commit('limitFollowedList')
    },
    async changeWorkingHours() {
      if (this.positiveNumberValid(this.currentWorkingHours)) {
        let res = await this.setUserSetting('default-working-hours-weekly', this.currentWorkingHours)
        if (res.status !== 'ok') this.showServerError(res)
      }
    },
    async changeVacationDays() {
      if (this.positiveNumberValid(this.currentVacationDays)) {
        let res1 = await this.setUserSetting('default-holiday-days-yearly', this.currentVacationDays)
        if (res1.status !== 'ok') this.showServerError(res1)

        let res2 = await this.setUserSetting('default-holiday-days-submit-year', moment().format('yyyy'))
        if (res2.status !== 'ok') this.showServerError(res2)
      }
    },
    async changeTimezone(e) {
      this.timezone = e
      this.settings.timezone = e
      Parse.User.current().set('timezone', e)
      Parse.User.current().save()
      this.$store.commit('setOneUserSetting', { name: 'timezone', value: e })
    },

    async changeUserStartOfWeek(day) {
      this.settings['week-start'] = day
      let payload = { name: 'week-start', value: day }
      await this.$store.dispatch('reloadUserSettings', payload)
    },
    async changeHourFormat(e) {
      let is24h = e === true ? true : false
      this.$store.commit('setOneUserSetting', { name: 'is-24h', value: is24h })
    },
  },
}
</script>

<style lang="scss" scoped>
section {
  display: grid;
  grid-auto-flow: row;
  grid-row-gap: 60px;
  //max-width: 550px;
}

.slot {
  display: flex;
  flex-direction: column;
}

@include respond-above(sm) {
  section {
    margin: 35px;
  }
  .slot {
    display: flex;
    flex-direction: column;
    //width: 220px;
    #work-schedule-deadline-list {
      width: 686px;
    }
  }
  #calendar-slot,
  #work-schedule-deadline-slot {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-column-gap: 8%;
    grid-template-rows: auto;

    #start-of-week {
      grid-row: 1;
      grid-column: 1;
    }

    #timezone {
      grid-row: 1;
      grid-column: 2;
    }

    /*#work-schedule-deadline-list {
            grid-row: 1;
            grid-column: 1 /span 3;
        }*/
  }
  #work-schedule-input,
  #time-off-input {
    width: 220px;
  }
}

@include respond-below(sm) {
  section {
    margin: 20px;
    margin-top: 35px;
  }
  .single-setting {
    //width: 322px;
  }
  #work-schedule-deadline-list {
    width: 100%;
  }
  .slot {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
}
</style>
